import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createClient, type ApiClient, type ClientOptions } from '@st/contract';
import type { Locale } from '@st/i18n';
import { API_URL, USE_MOCKS } from '../env';
import { createMockFetch } from '../mocks/fetch';
import type { Role, SessionUser } from '../api/types';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthValue {
  status: AuthStatus;
  user: SessionUser | null;
  api: ApiClient;
  login: (email: string, password: string) => Promise<SessionUser>;
  register: (input: {
    email: string;
    password: string;
    firstName: string;
    lastName?: string;
    locale: Locale;
  }) => Promise<SessionUser>;
  logout: () => Promise<void>;
  /** Lokalno osvježenje korisnika nakon promjene profila (jezik, onboarding). */
  patchUser: (patch: Partial<SessionUser>) => void;
  /**
   * Trenutni access token — potreban SAMO za WebSocket handshake, koji ne nosi
   * `Authorization` zaglavlje. Ostatak aplikacije token nikad ne dodiruje.
   */
  getAccessToken: () => string | null;
  setApiLocale: (locale: Locale) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Testovi injektiraju vlastiti transport ili gotovog klijenta. */
  fetchImpl?: typeof fetch;
  initialUser?: SessionUser | null;
  /**
   * Početni access token — koriste ga SAMO testovi, jer bez njega nema
   * WebSocket handshakea (token ide u query, ne u zaglavlje).
   */
  initialAccessToken?: string;
  /** Preskoči refresh na startu (testovi s poznatim korisnikom). */
  skipBootstrap?: boolean;
}

export function AuthProvider({
  children,
  fetchImpl,
  initialUser = null,
  initialAccessToken,
  skipBootstrap = false,
}: AuthProviderProps) {
  /**
   * Access token živi ISKLJUČIVO u memoriji — nikad u localStorage.
   * Osvježavanje ide preko HttpOnly kolačića na POST /v1/auth/refresh.
   */
  const tokenRef = useRef<string | null>(initialAccessToken ?? null);
  const clientRef = useRef<ApiClient | null>(null);
  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  const [user, setUser] = useState<SessionUser | null>(initialUser);
  const [status, setStatus] = useState<AuthStatus>(
    skipBootstrap ? (initialUser ? 'authenticated' : 'anonymous') : 'loading',
  );

  const optsRef = useRef<ClientOptions | null>(null);

  const api = useMemo<ApiClient>(() => {
    const transport = fetchImpl ?? (USE_MOCKS ? createMockFetch() : undefined);
    const opts: ClientOptions = {
      baseUrl: API_URL,
      locale: 'hr',
      getToken: () => tokenRef.current,
      onUnauthorized: async () => {
        // Jedan refresh po valu 401-ica; createClient ponavlja zahtjev točno jednom.
        refreshInFlight.current ??= (async () => {
          try {
            const session = await clientRef.current!.auth.refresh();
            tokenRef.current = session.accessToken;
            setUser(session.user);
            setStatus('authenticated');
            return session.accessToken;
          } catch {
            tokenRef.current = null;
            setUser(null);
            setStatus('anonymous');
            return null;
          } finally {
            refreshInFlight.current = null;
          }
        })();
        return refreshInFlight.current;
      },
      ...(transport ? { fetchImpl: transport } : {}),
    };
    optsRef.current = opts;
    const client = createClient(opts);
    clientRef.current = client;
    return client;
  }, [fetchImpl]);

  // Na startu pokušaj tihi refresh — sekcija 10: "sesija istekla → tiho osvježavanje".
  useEffect(() => {
    if (skipBootstrap) return;
    let cancelled = false;
    void (async () => {
      try {
        const session = await api.auth.refresh();
        if (cancelled) return;
        tokenRef.current = session.accessToken;
        setUser(session.user);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        tokenRef.current = null;
        setUser(null);
        setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, skipBootstrap]);

  const login = useCallback<AuthValue['login']>(
    async (email, password) => {
      const session = await api.auth.login({ email, password });
      tokenRef.current = session.accessToken;
      setUser(session.user);
      setStatus('authenticated');
      return session.user;
    },
    [api],
  );

  const register = useCallback<AuthValue['register']>(
    async (input) => {
      const session = await api.auth.register({
        email: input.email,
        password: input.password,
        firstName: input.firstName,
        ...(input.lastName ? { lastName: input.lastName } : {}),
        locale: input.locale,
        acceptTerms: true,
      });
      tokenRef.current = session.accessToken;
      setUser(session.user);
      setStatus('authenticated');
      return session.user;
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      tokenRef.current = null;
      setUser(null);
      setStatus('anonymous');
    }
  }, [api]);

  const patchUser = useCallback((patch: Partial<SessionUser>) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  const getAccessToken = useCallback(() => tokenRef.current, []);

  const setApiLocale = useCallback((locale: Locale) => {
    if (optsRef.current) optsRef.current.locale = locale;
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, user, api, login, register, logout, patchUser, getAccessToken, setApiLocale }),
    [status, user, api, login, register, logout, patchUser, getAccessToken, setApiLocale],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth mora biti unutar <AuthProvider>');
  return ctx;
}

export function useApi(): ApiClient {
  return useAuth().api;
}

/** Početna ruta po ulozi — jedna definicija za guard, preusmjeravanja i navigaciju. */
export const HOME_BY_ROLE: Record<Role, string> = {
  client: '/',
  trainer: '/trener',
  admin: '/admin',
};
